<template>
  <Card data-cy="firmware-form">
    <template #content>
      <div class="flex flex-col gap-8">
        <FormGroup :label="$t('forms.firmware.firmware_properties_form.version_number')"
                   :error-text="form.errors?.version_number">
          <InputText
              data-cy="version-number-input-box"
              ref="versionNumber"
              v-model="form.version_number"
              :placeholder="$t('common.example', { example: 'AB23XYZ' })"
              @change="form.validate('version_number')"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.firmware.firmware_properties_form.firmware_type')"
                   :error-text="form.errors?.firmware_type_id">
          <Dropdown
              data-cy="firmware-type-selection"
              v-model="form.firmware_type_id"
              :options="firmwareTypesStore.getFirmwareTypeOptions"
              option-label="name"
              option-value="id"
              @change="form.validate('firmware_type_id')"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.firmware.firmware_properties_form.device_type')"
                   :error-text="form.errors?.device_type_id">
          <Dropdown
              data-cy="device-type-selection"
              v-model="form.device_type_id"
              :options="deviceTypesStore.getDeviceTypeOptions"
              option-label="name"
              option-value="id"
              @change="onDeviceTypeChange"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.firmware.firmware_properties_form.device_model')"
                   :error-text="form.errors?.device_model_id">
          <Dropdown
              data-cy="device-model-selection"
              v-model="form.device_model_id"
              :options="deviceModels"
              option-label="name"
              option-value="id"
              @change="onDeviceModelChange"
          />
        </FormGroup>

        <FormGroup :label="$t('forms.firmware.firmware_properties_form.applicable_device_revisions')"
                   :error-text="form.errors?.device_revision_ids">
          <MultiSelect data-cy="applicable-device-revision-selection"
                       v-model="form.device_revision_ids"
                       :options="deviceRevisions"
                       optionLabel="name"
                       :placeholder="$t('forms.firmware.firmware_properties_form.select_device_revisions')"
                       display="chip"
                       class="w-full md:w-20rem"
                       option-value="id"
                       :disabled="!form.device_type_id"
          >
            <template #option="slotProps">
              <div class="flex align-items-center">
                <div>{{ slotProps.option.name }} - <span class="font-light">{{ slotProps.option.description }}</span>
                </div>
              </div>
            </template>
            <template #footer>
              <div class="py-2 px-3">
                {{
                  $t('forms.common.x_items_selected', form.device_revision_ids?.length ?? 0, {count: form.device_revision_ids?.length ?? 0})
                }}
              </div>
            </template>
          </MultiSelect>
        </FormGroup>

        <FormGroup :label="$t('forms.firmware.firmware_properties_form.relevant_regulations')"
                   :error-text="form.errors?.regulation_ids">
          <MultiSelect data-cy="relevant-regulation-selection" v-model="form.regulation_ids"
                       :options="regulations"
                       optionLabel="name"
                       :placeholder="$t('forms.firmware.firmware_properties_form.select_regulations')"
                       display="chip"
                       class="w-full md:w-20rem"
                       option-value="id"
          >
            <template #option="slotProps">
              <div class="flex align-items-center">
                <div>{{ slotProps.option.name }} - <span class="font-light">{{ slotProps.option.description }}</span>
                </div>
              </div>
            </template>
            <template #footer>
              <div class="py-2 px-3">
                {{
                  $t('forms.common.x_items_selected', form.regulation_ids?.length ?? 0, {count: form.regulation_ids?.length ?? 0})
                }}
              </div>
            </template>
          </MultiSelect>
        </FormGroup>

        <FormGroup :label="$t('forms.firmware.firmware_properties_form.firmware_file')" v-if="selectedFirmware?.name">
          <div class="rounded-md border border-surface-300 bg-surface-0 dark:bg-surface-800 dark:border-surface-700">
            <ul role="list" class="divide-y divide-surface-300">
              <li class="px-4 py-4 flex space-x-2 items-center">
                <FileTypeIcon :mime-type="selectedFirmware.mime_type"/>
                <div class="flex-1">
                  <h3 class="flex-auto truncate text-sm font-medium leading-4 text-surface-900 dark:text-surface-200">{{
                      selectedFirmware.name
                    }}</h3>
                  <p class="block text-sm text-surface-600 dark:text-surface-400">
                    {{ useFilesize(selectedFirmware.size_bytes) }}</p>
                </div>

                <Button
                    type="button"
                    @click="toggle"
                    severity="secondary"
                    size="small"
                    text
                    aria-haspopup="true"
                    aria-controls="overlay_menu"
                >
                  <template #icon>
                    <IconDots/>
                  </template>
                </Button>
                <Menu ref="menu" id="overlay_menu" :model="[
										{ label: $t('forms.common.download'), icon: 'FileTypeIcon', command: (e) => { this.$emit('on-download', this.selectedFirmware.id)} },
										{ label: $t('forms.common.download_signature'), icon: 'IconFileCertificate', command: (e) => { this.$emit('on-download-sig', this.selectedFirmware.id)} },
								]" :popup="true">
                  <template #itemicon="{ item, class: classes }">
                    <component v-if="item.icon" :is="item.icon" :classes="classes" class="mr-2"
                               :mime-type="selectedFirmware.mime_type"/>
                  </template>
                </Menu>
              </li>
            </ul>
          </div>
        </FormGroup>

        <FileInput
            v-if="!selectedFirmware?.name"
            v-model="form.firmware_file"
            :label="$t('forms.firmware.firmware_properties_form.firmware_file')"
            :help-text="firmwareFileHelpText"
            :error-text="form.errors?.firmware_file"
            @change="form.validate('firmware_file')"
        />

        <FormGroup :label="$t('forms.firmware.firmware_properties_form.purpose')"
        :help-text="$t('forms.firmware.firmware_properties_form.purpose_help_text')"
               :error-text="form.errors?.purpose">
            <div class="flex flex-col gap-2">
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-2">
                <input data-cy="yes-option-firmware-internal-use" type="radio"
                v-model="form.purpose"
                value="yes_internal"
                name="yes_internal"
                id="yes_internal"
                @change="form.validate('purpose')"
                />
                <label for="yes_internal">
                  {{ $t('forms.firmware.firmware_properties_form.yes_internal') }}
                </label>
              </div>
            </div>
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-2">
                <input data-cy="no-option-firmware-internal-use" type="radio"
                v-model="form.purpose"
                value="not_internal"
                name="not_internal"
                id="not_internal"
                @change="form.validate('purpose')"
                />
                <label for="not_internal">
                  {{ $t('forms.firmware.firmware_properties_form.not_internal') }}
                </label>
              </div>
            </div>
          </div>
          
        </FormGroup>
        <FormGroup v-if="form.has_addr_info && selectedFirmware?.name" :label="$t('forms.firmware.firmware_properties_form.write_additional_data_to_file_on_download')"
                   :help-text="$t('forms.firmware.firmware_properties_form.enable_these_options_to_append_additional_data_to_the_srecord_file')"
                   help-text-position-top
        >
          <div class="grid grid-cols-3 space-x-4">
            <InputSwitchFormGroup
                :label="$t('forms.firmware.firmware_properties_form.write_serial_number')"
                :help-text="$t('forms.firmware.firmware_properties_form.write_serial_number_help_text')"
                :error-text="form.errors?.write_serial_number"
                :disabled="!form.write_serial_number_enabled"

                @change="form.validate('write_serial_number')"
                v-model="form.write_serial_number"
                :true-value="1"
                :false-value="0"
            />
            <InputSwitchFormGroup
                :label="$t('forms.firmware.firmware_properties_form.write_signature')"
                :help-text="$t('forms.firmware.firmware_properties_form.write_signature_help_text')"
                :error-text="form.errors?.write_signature"
                :disabled="!form.write_signature_enabled"

                @change="form.validate('write_signature')"
                v-model="form.write_signature"
                :true-value="1"
                :false-value="0"
            />
            <InputSwitchFormGroup
                :label="$t('forms.firmware.firmware_properties_form.write_timestamp')"
                :help-text="$t('forms.firmware.firmware_properties_form.write_timestamp_help_text')"
                :error-text="form.errors?.write_timestamp"
                :disabled="!form.write_timestamp_enabled"

                @change="form.validate('write_timestamp')"
                v-model="form.write_timestamp"
                :true-value="1"
                :false-value="0"
            />
          </div>
        </FormGroup>

        <Message v-if="!form.has_addr_info && selectedFirmware?.name" :closable="false" severity="info" class="max-w-3xl">
          <i18n-t keypath="forms.firmware.firmware_properties_form.additional_data_instructions" tag="div" class="leading-5" scope="global">
            <template #serial_number>serial_number</template>
            <template #filename><strong>addr_info.txt</strong></template>
            <template #documentation><a :href="userGuideUrl" target="_blank" class="font-bold underline">{{ $t('forms.firmware.firmware_properties_form.documentation') }}</a></template>
          </i18n-t>
        </Message>
      </div>
    </template>

    <template v-if="hasOnSaveListener || hasOnDeleteListener" #footer>
      <div class="flex justify-end space-x-3">
        <Button data-cy="cancel-button-firmware" class="px-8"
                text
                severity="danger"
                v-if="hasOnDeleteListener && form.id" @click="$emit('on-delete', form)"
                :label="$t('forms.common.delete')"
                :disabled="form.processing"/>
        <Button data-cy="view-signature-button" class="px-8"
                severity="secondary"
                v-if="selectedFirmware?.id && !isProduction"
                @click="$emit('on-view-signature', selectedFirmware.id)"
                :label="$t('forms.firmware.firmware_properties_form.view_signature')"
                :disabled="form.processing"/>
        <Button data-cy="save-button" class="px-8" v-if="hasOnSaveListener" @click="$emit('on-save', form)" :label="$t('forms.common.save')"
                :disabled="form.processing"/>
      </div>
    </template>
  </Card>
</template>

<script>
import {mapStores} from "pinia";
import {useDeviceModelsStore} from "@/stores/deviceModels.js";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useDeviceTypesStore} from "@/stores/deviceTypes.js";
import {useFirmwareTypesStore} from "@/stores/firmwareTypes.js";
import {IconDots, IconFileCertificate, IconFileCode, IconFileTypePdf} from '@tabler/icons-vue';
import FileInput from "@/Components/FileInput.vue";
import FormGroup from "@/Components/FormGroup.vue";
import {useFilesize} from "@/Composables/Filesize.js";
import FileTypeIcon from "@/Components/FileTypeIcon.vue";
import {useRegulationsStore} from "@/stores/regulations.js";
import InputSwitchFormGroup from "@/Components/InputSwitchFormGroup.vue";

export default {
  name: "FirmwarePropertiesForm",
  methods: {
    useFilesize,
    toggle(e) {
      this.$refs.menu.toggle(e);
    },
    onDeviceTypeChange() {
      this.form.device_model_id = null;
      this.onDeviceModelChange();
    },
    onDeviceModelChange() {
      this.form.device_revision_ids = [];
      this.form.validate();
    }
  },
  components: {
    InputSwitchFormGroup,
    FileTypeIcon,
    FormGroup,
    FileInput,
    IconDots, IconFileCode, IconFileTypePdf, IconFileCertificate
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    selectedFirmware: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      file: null,
      userGuideUrl: import.meta.env.VITE_USER_GUIDE_URL,
    }
  },
  computed: {
    firmwareFileHelpText() {
      const fileTypes = import.meta.env.VITE_APP_ENV === 'internal'
          ? 'exe, bin, run, zip, tar, gz, tgz, rar or srec'
          : 'exe, bin, run, zip, tar, gz, tgz or rar';

      return `Please upload an ${fileTypes} file that is less than 100mb in size`;
    },
    ...mapStores(useDeviceModelsStore),
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useDeviceTypesStore),
    ...mapStores(useFirmwareTypesStore),
    ...mapStores(useRegulationsStore),
    hasOnSaveListener() {
      return !!(this.$attrs?.onOnSave);
    },
    hasOnDeleteListener() {
      return !!(this.$attrs?.onOnDelete);
    },
    isProduction() {
      return import.meta.env.VITE_APP_ENV === 'production';
    },
    deviceModels() {
      return this.deviceModelsStore.getDeviceModelOptions?.filter(model => model.device_type_id === this.form.device_type_id);
    },
    deviceRevisions() {
      return this.deviceRevisionsStore.getDeviceRevisionOptions?.filter(revision => revision.device_model_id === this.form.device_model_id);
    },
    regulations() {
      if (!this.form.device_revision_ids.length)
        return [];

      return this.deviceRevisions
          ?.filter(revision => this.form.device_revision_ids.includes(revision.id))
          ?.map(revision => revision.regulations)
          ?.flat() || [];
    }
  },
  async beforeMount() {
    await this.firmwareTypesStore.indexFirmwareTypeOptions();
    await this.deviceModelsStore.indexDeviceModelOptions();
    await this.deviceRevisionsStore.indexDeviceRevisionOptions({
      include: ['regulations'],
    });
    await this.deviceTypesStore.indexDeviceTypeOptions();
  },
  mounted() {
    this.$refs.versionNumber.$el.focus();
  },
  watch: {
    'form.device_revision_ids': {
      handler: function (newValue, oldValue) {
        if (newValue?.length < oldValue?.length){
          // remove out any regulations which no longer exist...
          this.form.regulation_ids = this.form.regulation_ids.filter(reg_id => this.regulations.some(r => r.id === reg_id));
        }
      },
    },
  },
}
</script>

<style scoped>

</style>
